/**
 * SEO Sprint 4C (Feb 2026) — FAQ search bar.
 *
 * Compact zoekbalk bovenaan /faq voor client-side filteren door de
 * 77 FAQ-vragen. Controlled component — parent houdt de query bij en
 * geeft de filter-resultaat-count terug via `resultCount` prop.
 *
 * Geen server-request: alle filtering gebeurt client-side over de data
 * die FAQCategorySection toch al ophaalt.
 */
import React, { useRef } from "react";
import { Search, X } from "lucide-react";

export default function FAQSearchBar({
  value = "",
  onChange,
  resultCount = null,
  totalCount = 0,
  testid = "faq-search",
}) {
  const inputRef = useRef(null);
  const hasQuery = (value || "").trim().length > 0;

  const handleClear = () => {
    onChange?.("");
    // re-focus for fast follow-up typing
    if (inputRef.current) inputRef.current.focus();
  };

  return (
    <div
      className="bg-white border-b border-gray-100 py-4 sm:py-5"
      data-testid={`${testid}-strip`}
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative">
          <Search
            className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1a5193] pointer-events-none"
            aria-hidden="true"
          />
          <input
            ref={inputRef}
            type="search"
            value={value}
            onChange={(e) => onChange?.(e.target.value)}
            placeholder="Zoek in veelgestelde vragen, bijvoorbeeld: douane, betalen, tracking, paklijst…"
            aria-label="Zoek in veelgestelde vragen"
            className="w-full pl-10 pr-24 sm:pr-28 py-3 sm:py-3.5 rounded-2xl bg-slate-50 border border-slate-200 text-sm text-[#082c50] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1a5193]/30 focus:border-[#1a5193] transition"
            data-testid={`${testid}-input`}
          />
          {hasQuery ? (
            <button
              type="button"
              onClick={handleClear}
              aria-label="Zoekterm wissen"
              className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs text-gray-500 hover:text-[#082c50] hover:bg-slate-100 transition-colors"
              data-testid={`${testid}-clear`}
            >
              <X className="w-3.5 h-3.5" aria-hidden="true" />
              <span className="hidden sm:inline">Wissen</span>
            </button>
          ) : null}
        </div>

        {/* Result count line — alleen tonen bij actieve zoekterm */}
        {hasQuery ? (
          <div
            className="mt-2.5 text-xs text-gray-500 leading-snug"
            data-testid={`${testid}-result-count`}
            aria-live="polite"
          >
            {resultCount === null ? (
              <span>Zoeken in {totalCount} vragen…</span>
            ) : resultCount === 0 ? (
              <span className="text-red-600 font-semibold">
                Geen vragen gevonden voor "{value.trim()}"
              </span>
            ) : (
              <span>
                {resultCount} van {totalCount} vragen gevonden voor{" "}
                <strong className="text-[#082c50]">"{value.trim()}"</strong>
              </span>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
